import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  FaTimes,
  FaDownload,
  FaFileExcel,
  FaSpinner,
  FaCheckCircle,
  FaColumns,
  FaListUl
} from 'react-icons/fa';
import * as XLSX from 'xlsx';

// ── Date cells for exports ───────────────────────────────────────────────────
// Exports show dates as dd-mm-yyyy (datetimes as "dd-mm-yyyy hh:mm AM"). The
// value is returned as a plain string so json_to_sheet writes a text cell and
// Excel cannot reinterpret it as a US month-first date.

const pad2 = (n: number) => String(n).padStart(2, '0');

const toDmy = (d: Date) => `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;

// A bare YYYY-MM-DD is a calendar day: read it as written, never through the
// browser's time zone (new Date('2026-10-08') is UTC midnight).
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

const parseExportDate = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const text = String(value).trim();
  const dayOnly = DATE_ONLY.exec(text);
  if (dayOnly) return new Date(Number(dayOnly[1]), Number(dayOnly[2]) - 1, Number(dayOnly[3]));
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** dd-mm-yyyy, or `fallback` when the value is empty or not a date. */
export const formatExcelDate = (value: unknown, fallback = ''): string => {
  const d = parseExportDate(value);
  return d ? toDmy(d) : fallback;
};

/** dd-mm-yyyy hh:mm AM, or `fallback` when the value is empty or not a date. */
export const formatExcelDateTime = (value: unknown, fallback = ''): string => {
  const d = parseExportDate(value);
  if (!d) return fallback;
  const hours = d.getHours();
  return `${toDmy(d)} ${pad2(hours % 12 || 12)}:${pad2(d.getMinutes())} ${hours < 12 ? 'AM' : 'PM'}`;
};

interface ExcelDownloadProps {
  isOpen: boolean;
  onClose: () => void;
  data: any[];
  filename?: string;
  title?: string;
  description?: string;
  /** Label/value lines appended below the data, after one blank row
   *  (e.g. "Total sites fetched"). Exported in both XLSX and CSV. */
  summaryRows?: Array<{ label: string; value: string | number }>;
}

export const ExcelDownload: React.FC<ExcelDownloadProps> = ({
  isOpen,
  onClose,
  data,
  filename = 'export_data',
  title = 'Export Data',
  description = 'Download your data in Excel format',
  summaryRows = []
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState<'xlsx' | 'csv'>('xlsx');
  const [exportMode, setExportMode] = useState<'selected' | 'all'>('selected');
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  const DEFAULT_ESSENTIAL_COLUMNS = ['Site ID', 'Project', 'Status'];

  const columnList = useMemo(() => {
    if (!data || data.length === 0) return [];
    return Object.keys(data[0]);
  }, [data]);

  const essentialColumns = useMemo(() => {
    if (!columnList.length) return [];
    const matchedDefaults = DEFAULT_ESSENTIAL_COLUMNS.filter((column) => columnList.includes(column));
    if (matchedDefaults.length >= 3 || columnList.length <= 3) {
      return matchedDefaults.length ? matchedDefaults : columnList.slice(0, Math.min(3, columnList.length));
    }
    const fallbacks = columnList.filter((col) => !matchedDefaults.includes(col)).slice(0, 3 - matchedDefaults.length);
    return [...matchedDefaults, ...fallbacks];
  }, [columnList]);

  const optionalColumns = useMemo(
    () => columnList.filter((column) => !essentialColumns.includes(column)),
    [columnList, essentialColumns]
  );

  useEffect(() => {
    setSelectedColumns((prev) => prev.filter((column) => optionalColumns.includes(column)));
  }, [optionalColumns]);

  const columnsToExport = useMemo(() => {
    if (exportMode === 'all') {
      return columnList;
    }
    const extras = selectedColumns.filter((column) => optionalColumns.includes(column));
    const combined = [...essentialColumns, ...extras];
    return Array.from(new Set(combined));
  }, [columnList, essentialColumns, optionalColumns, selectedColumns, exportMode]);

  if (!isOpen) return null;

  const buildFilteredRows = (columns: string[]) => {
    return data.map((row) => {
      const filteredRow: Record<string, any> = {};
      columns.forEach((column) => {
        filteredRow[column] = row[column];
      });
      return filteredRow;
    });
  };

  const ensureDownloadPreconditions = () => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return false;
    }

    if (!columnsToExport.length) {
      toast.error('Select at least one column to export');
      return false;
    }

    return true;
  };

  const downloadAsExcel = async () => {
    if (!ensureDownloadPreconditions()) return;

    setIsDownloading(true);

    try {
      if (downloadFormat === 'csv') {
        downloadAsCSV(columnsToExport);
      } else {
        downloadAsXLSX(columnsToExport);
      }
    } catch (error) {
      console.error('Download failed:', error);
      const message = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Download failed: ${message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const getDateSuffix = () => toDmy(new Date());

  const downloadAsCSV = (columns: string[]) => {
    if (!data || data.length === 0 || columns.length === 0) return;

    // Create CSV content
    let csvContent = columns.join(',') + '\n';
    
    data.forEach(row => {
      const values = columns.map(header => {
        const value = row[header];
        // Escape commas and quotes in values
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      });
      csvContent += values.join(',') + '\n';
    });

    if (summaryRows.length > 0) {
      const csvCell = (value: string | number) =>
        typeof value === 'string' && (value.includes(',') || value.includes('"'))
          ? '"' + value.replace(/"/g, '""') + '"'
          : value;
      csvContent += '\n';
      summaryRows.forEach(({ label, value }) => {
        csvContent += csvCell(label) + ',' + csvCell(value) + '\n';
      });
    }

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${getDateSuffix()}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Close modal after download
    setTimeout(() => {
      onClose();
    }, 500);
  };

  const downloadAsXLSX = (columns: string[]) => {
    if (!data || data.length === 0 || columns.length === 0) return;

    // XLSX cells cannot exceed 32,767 characters — truncate any value that does.
    const XLSX_MAX_CELL_LENGTH = 32767;
    const sanitizeCell = (value: unknown): unknown => {
      if (typeof value === 'string' && value.length > XLSX_MAX_CELL_LENGTH) {
        return value.slice(0, XLSX_MAX_CELL_LENGTH - 3) + '...';
      }
      return value;
    };

    const filteredRows = buildFilteredRows(columns).map((row) => {
      const sanitized: Record<string, unknown> = {};
      columns.forEach((col) => {
        sanitized[col] = sanitizeCell(row[col]);
      });
      return sanitized;
    });

    const worksheet = XLSX.utils.json_to_sheet(filteredRows, { header: columns });
    if (summaryRows.length > 0) {
      XLSX.utils.sheet_add_aoa(
        worksheet,
        [[], ...summaryRows.map(({ label, value }) => [label, value])],
        { origin: -1 }
      );
    }
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sites');
    XLSX.writeFile(workbook, `${filename}_${getDateSuffix()}.xlsx`);

    setTimeout(() => {
      onClose();
    }, 500);
  };

  const formatDataPreview = () => {
    if (!data || data.length === 0) return 'No data available';
    
    const previewCount = Math.min(3, data.length);
    const headers = columnsToExport.length ? columnsToExport : columnList;
    const MAX_PREVIEW_COLUMNS = 10;
    const previewHeaders = headers.slice(0, MAX_PREVIEW_COLUMNS);
    const trimmedColumns = headers.length - previewHeaders.length;
    
    return (
      <div className="text-left">
        <div className="text-xs font-medium text-gray-600 mb-2">
          Preview ({previewCount} of {data.length} rows)
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-xs font-mono border border-gray-100">
          <div className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <FaListUl className="w-3.5 h-3.5 text-gray-500" />
            {previewHeaders.join(' | ')}
            {trimmedColumns > 0 && (
              <span className="text-gray-400 text-[10px] whitespace-nowrap">
                +{trimmedColumns} more columns hidden
              </span>
            )}
          </div>
          {data.slice(0, previewCount).map((row, index) => (
            <div key={index} className="text-gray-600 truncate">
              {previewHeaders.map(header => String(row[header] ?? '')).join(' | ')}
            </div>
          ))}
          {trimmedColumns > 0 && (
            <div className="text-gray-500 text-[10px] mt-2 text-left">
              Showing first {MAX_PREVIEW_COLUMNS} columns only
            </div>
          )}
          {data.length > previewCount && (
            <div className="text-gray-500 text-center mt-2">
              ... and {data.length - previewCount} more rows
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleToggleColumn = (column: string) => {
    setSelectedColumns((prev) => {
      if (prev.includes(column)) {
        return prev.filter((item) => item !== column);
      }
      return [...prev, column];
    });
  };

  const handleSelectAllOptional = () => {
    if (!optionalColumns.length) return;
    const allSelected = optionalColumns.every((column) => selectedColumns.includes(column));
    setSelectedColumns(allSelected ? [] : optionalColumns);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={onClose} />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-50 via-emerald-50 to-white px-6 py-4 border-b border-green-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <FaFileExcel className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                  <p className="text-sm text-gray-600">{description}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-5 space-y-5">
            <div className="grid gap-5 lg:grid-cols-2">
              <div className="border border-gray-100 rounded-xl p-4 bg-white shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-medium text-gray-800 flex items-center gap-2">
                      <FaColumns className="text-green-600" />
                      Included Columns
                    </p>
                    <p className="text-xs text-gray-500">Site ID and two key columns are always included.</p>
                  </div>
                  <span className="text-xs font-medium text-gray-500">
                    {columnsToExport.length} / {columnList.length || 0} columns
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {essentialColumns.map((column) => (
                    <span
                      key={column}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100"
                    >
                      <FaCheckCircle className="w-3.5 h-3.5" />
                      {column}
                    </span>
                  ))}
                </div>
                {optionalColumns.length > 0 ? (
                  <div className="mt-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
                      <span className="text-sm font-medium text-gray-700">Add more columns</span>
                      <button
                        type="button"
                        onClick={handleSelectAllOptional}
                        disabled={exportMode === 'all'}
                        className="text-xs font-medium text-green-600 hover:text-green-700 disabled:text-gray-400"
                      >
                        {selectedColumns.length === optionalColumns.length ? 'Clear all' : 'Select all'}
                      </button>
                    </div>
                    <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                      {optionalColumns.map((column) => (
                        <label
                          key={column}
                          className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700"
                        >
                          <input
                            type="checkbox"
                            checked={exportMode === 'all' ? true : selectedColumns.includes(column)}
                            disabled={exportMode === 'all'}
                            onChange={() => handleToggleColumn(column)}
                            className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 disabled:bg-gray-100"
                          />
                          <span className={exportMode === 'all' ? 'text-gray-400' : ''}>{column}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="mt-4 text-xs text-gray-500">All available columns are already essential.</p>
                )}
              </div>

              <div className="space-y-4">
                <div className="border border-gray-100 rounded-xl p-4 bg-white shadow-sm">
                  <p className="text-sm font-medium text-gray-800 mb-3">Export Scope</p>
                  <div className="space-y-2">
                    <label className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="exportScope"
                        value="selected"
                        checked={exportMode === 'selected'}
                        onChange={() => setExportMode('selected')}
                        className="mt-1 h-4 w-4 text-green-600 border-gray-300 focus:ring-green-500"
                      />
                      <div>
                        <p className="text-sm text-gray-800 font-medium">Only essential + chosen columns</p>
                        <p className="text-xs text-gray-500">Downloads Site ID, Project, Status and any extra columns you pick.</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="exportScope"
                        value="all"
                        checked={exportMode === 'all'}
                        onChange={() => setExportMode('all')}
                        className="mt-1 h-4 w-4 text-green-600 border-gray-300 focus:ring-green-500"
                      />
                      <div>
                        <p className="text-sm text-gray-800 font-medium">Complete dataset</p>
                        <p className="text-xs text-gray-500">Download every available column as shown in the table.</p>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="border border-gray-100 rounded-xl p-4 bg-white shadow-sm">
                  {formatDataPreview()}
                </div>

                <div className="border border-blue-100 bg-blue-50 rounded-xl p-4">
                  <div className="flex justify-between text-sm text-blue-900">
                    <span>Total Records</span>
                    <span className="font-medium">{data?.length || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm text-blue-900 mt-1">
                    <span>Columns in download</span>
                    <span className="font-medium">{columnsToExport.length}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
              {/* Format Selection */}
              <div className="lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Export Format
                </label>
                <div className="flex gap-3">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="format"
                      value="csv"
                      checked={downloadFormat === 'csv'}
                      onChange={(e) => setDownloadFormat(e.target.value as 'csv')}
                      className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">CSV</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="format"
                      value="xlsx"
                      checked={downloadFormat === 'xlsx'}
                      onChange={(e) => setDownloadFormat(e.target.value as 'xlsx')}
                      className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Excel (XLSX)</span>
                  </label>
                </div>
              </div>

              {/* File Name */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File Name
                </label>
                <div className="flex items-center">
                  <input
                    type="text"
                    value={filename}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md bg-gray-50 text-sm"
                  />
                  <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md text-sm text-gray-600">
                    .{downloadFormat}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded  hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={downloadAsExcel}
                disabled={isDownloading || !data || data.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {isDownloading ? (
                  <>
                    <FaSpinner className="w-4 h-4 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <FaDownload className="w-4 h-4" />
                    Download {downloadFormat.toUpperCase()}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};